import { FC, useEffect, useCallback, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { clearQuiz } from '@app/store/slices/quiz'
import Zendesk, { ZendeskAPI } from 'react-zendesk'
import urls, { getDocumentTitle } from '@app/routes/urls'
import { IntlProvider } from 'react-intl'
import AlertProvider from '@app/shared/Alerts/AlertProvider'
import { AnalyticsProvider } from '@app/shared/Analytics'
import useStaticCampaign from '@app/hooks/useStaticCampaign'
import useDynamicCampaign from '@app/hooks/useDynamicCampaign'
import { Helmet } from 'react-helmet'
import { isLanguageObject } from '@app/lang/utils'
import useCampaignHref from '@app/hooks/useCampaignHref'
import { actions, CampaignStatus } from '@app/store/slices/campaign'
import useMinimumLoadingTime from '@app/hooks/useMinimumLoadingTime'
import SmoothLoading from '@app/components/SmoothLoading'
import { AnimatePresence } from 'framer-motion'
import { useTimeout } from 'usehooks-ts'
import CookieProvider from './CookieConsent/CookieProvider'
import useCookieConsent from './CookieConsent/useCookieConsent'

const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  const { values } = useCookieConsent()

  return (
    <AnalyticsProvider
      trackEssential
      trackFunctional={values?.functional}
      trackPerformance={values?.performance}
      trackTargeting={values?.targetting}>
      {children}
    </AnalyticsProvider>
  )
}

const CustomerApp: FC = () => {
  const location = useLocation()
  const params = useParams<{ program: string; campaign?: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { language } = useAppSelector(state => state.settings)
  const { href: closedUrl } = useCampaignHref({ requestPath: urls.closed })

  /**
   * Flag whether the campaign code is provided in the URL
   * - If TRUE, then the campaign data will be requested from the API
   * - If FALSE, then the campaign data will be statically loaded from the program data
   */
  const requestedDynamicCampaign = useMemo(() => {
    return params.campaign !== undefined
  }, [params.campaign])

  const staticCampaign = useStaticCampaign(!requestedDynamicCampaign)
  const dynamicCampaign = useDynamicCampaign(requestedDynamicCampaign)
  const campaign = useMemo(
    () => (requestedDynamicCampaign ? dynamicCampaign : staticCampaign),
    [dynamicCampaign, staticCampaign, requestedDynamicCampaign]
  )

  // Translate Zendesk Widget to DE on load
  const translate = useCallback(() => {
    ZendeskAPI('webWidget', 'setLocale', language)
  }, [language])

  // Wipe the Quiz when navigating away from it
  useEffect(() => {
    if (!location.pathname.includes('quiz') && !location.pathname.includes('order')) {
      dispatch(clearQuiz())
    }
  }, [dispatch, location.pathname])

  // Translate the widget when the language changes
  useEffect(() => {
    translate()
  }, [translate])

  // Redirect to the 404 page if the program is not found
  useEffect(() => {
    if (campaign.notFound) {
      navigate(`/${urls.notFound}`, { replace: true })
    }
  }, [campaign, navigate])

  // Redirect to the campaign closed page if the campaign is ended
  useEffect(() => {
    if (campaign.ended) {
      dispatch(actions.setStatus(CampaignStatus.Closed))
      navigate(closedUrl, { replace: true })
    }
  }, [dispatch, navigate, campaign, closedUrl])

  /** The translated suffix shown in the tab title */
  const pageTitleSuffix = 'document_title_suffix' in campaign.messages ? campaign.messages.document_title_suffix : ''

  /** The translated page name shown in the tab title */
  const pageTitle = useMemo(
    () => (isLanguageObject(campaign.messages) ? getDocumentTitle(location.pathname, campaign.messages) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, language]
  )

  // Only show loading during initial load, not during language changes
  const showLoading = useMinimumLoadingTime(campaign.isInitialLoad && !campaign.messagesLoaded, 2000)

  /** Add a buffer for showing the main content on the initial load to prevent CLS */
  const [showContent, setShowContent] = useState(false)
  useTimeout(() => setShowContent(true), 500)

  return (
    <>
      <AnimatePresence mode="wait">{showLoading && <SmoothLoading locale={language} />}</AnimatePresence>

      <AnimatePresence mode="wait">
        {showContent && (
          <IntlProvider locale={language} messages={campaign.messages}>
            <Helmet htmlAttributes={{ lang: language }} titleTemplate={`%s | ${pageTitleSuffix}`}>
              <title>{pageTitle}</title>
            </Helmet>
            <AlertProvider>
              <CookieProvider>
                <AnalyticsWrapper>
                  {params.program && !campaign.ended && <Zendesk defer zendeskKey={ZENDESK_KEY} onLoaded={translate} />}
                  <Outlet />
                </AnalyticsWrapper>
              </CookieProvider>
            </AlertProvider>
          </IntlProvider>
        )}
      </AnimatePresence>
    </>
  )
}

export default CustomerApp
